import { Bounds, MapControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { parseAsNativeArrayOf, parseAsString, useQueryState } from "nuqs";
import { Suspense, useEffect } from "react";
import { AgXToneMapping } from "three";

import { FancySpinner } from "../components/common/fancy-spinner.js";
import { FeedbackButton } from "../components/ui/feedback-button.js";
import { FluidConnectionStatusIndicator } from "../components/ui/fluid-connection-status-indicator.js";
import { ModalRenderer } from "../components/ui/modals/modal-renderer.js";
import { NewVersionAvailableNotification } from "../components/ui/notifications/new-version-available-notification.js";
import { OfficeName } from "../components/ui/office-name.jsx";
import { BottomMenu } from "../components/ui/office/bottom-menu/bottom-menu.js";
import { ChatWindow } from "../components/ui/office/chat-window.js";
import { SidebarAppWarning } from "../components/ui/office/sidebar-app-warning.js";
import { RightSideMenu } from "../components/ui/right-side-menu.js";
import { ContextMenuRenderer } from "../features/context-menu/context-menu-renderer.js";
import { ContextMenuScreenBridge } from "../features/context-menu/context-menu-screen-bridge.js";
import { AvatarMenuWindow } from "../features/floating-window/avatar-menu.js";
import { RosterWindow } from "../features/floating-window/roster-window.js";
import { OFFICE_CAMERA_LIGHT_POSITIONS } from "../features/office-camera/camera.constants.js";
import { OfficeCameraControlsGuard } from "../features/office-camera/office-camera-controls-guard.js";
import {
  OfficeCameraViewportReader,
  OfficeSceneBoundsReader,
} from "../features/office-camera/office-camera-scene-readers.js";
import { useOfficeCamera } from "../features/office-camera/use-office-camera.js";
import { CameraDebugger } from "../features/office-debug/camera-debugger.js";
import { FpsMonitor } from "../features/office-fps-limit/fps-monitor.js";
import { OfficeLoader } from "../features/office-scene/office-loader.js";
import { OfficeScene } from "../features/office-scene/office-scene.js";
import { useOffice } from "../features/office/use-office.js";
import { usePlatform } from "../features/platform-detection/use-platform.js";
import { InactivityDetector } from "../features/presence/inactivity-detector.js";
import { useAudio } from "../hooks/use-audio.js";
import { useBezonedContext } from "../hooks/use-bezoned-context.js";
import { useDeviceInfo } from "../hooks/use-device-info.js";
import { useLayoutStore } from "../store/layout.store.js";

export const OfficePage = () => {
  const [debugMode] = useQueryState(
    "debug",
    parseAsNativeArrayOf(parseAsString)
  );

  const { ContextBridge } = useBezonedContext();
  useOffice();
  const applyChatDefaultPreference = useLayoutStore(
    (state) => state.applyChatDefaultPreference
  );
  const setModalVariant = useLayoutStore((state) => state.setModalVariant);
  const useSimplifiedOfficePreference = useLayoutStore(
    (state) => state.useSimplifiedOfficePreference
  );
  const { isMobile } = useDeviceInfo();
  const useSimplifiedOffice = useSimplifiedOfficePreference ?? isMobile;
  const officeCamera = useOfficeCamera({ topDown: useSimplifiedOffice });
  const { testAudioPermissions } = useAudio();
  const { isTeamsWebClient, isM365OrOutlookWebClient } = usePlatform();

  useEffect(() => {
    applyChatDefaultPreference();
  }, [applyChatDefaultPreference]);

  useEffect(() => {
    const checkAudioPermissions = async () => {
      const result = await testAudioPermissions();

      // ! M365 and Outlook Web Clients don't support App Permissions. They prevent us from accessing the microphone,
      // ! but there is no way to open App Permissions in Outlook and it does not open anything in M365.
      // ! Due to that, we don't have access to the microphone.
      // ! We should not be opening this modal in M365 and Outlook, because it will confuse the user and they cannot do anything about it.
      // ? Maybe a different one, which tells them the microphone cannot be used in M365 and Outlook would work?
      if (
        !isM365OrOutlookWebClient() &&
        (!result.browserPermissions ||
          (isTeamsWebClient() && !result.teamsPermissions))
      ) {
        setModalVariant("audio_permissions");
      }
    };

    checkAudioPermissions();
  }, [
    testAudioPermissions,
    isM365OrOutlookWebClient,
    isTeamsWebClient,
    setModalVariant,
  ]);

  return (
    <div className="office-background relative h-screen w-screen">
      {!officeCamera.isSceneReady && (
        <div className="office-background absolute inset-0 z-20 flex flex-col items-center justify-center gap-4">
          <FancySpinner />
          <span className="text-lg text-gray-600">Loading office...</span>
        </div>
      )}

      <Canvas
        className={officeCamera.isSceneReady ? "z-0" : "invisible z-0"}
        frameloop="demand"
        gl={{ alpha: true, toneMapping: AgXToneMapping }}
        shadows
      >
        <ContextBridge>
          <OfficeCameraViewportReader onReady={officeCamera.setViewport} />
          <ContextMenuScreenBridge />
          <FpsMonitor show={debugMode.includes("fps")} />

          <mesh rotation={[0, 0, 0]}>
            <ambientLight
              intensity={officeCamera.viewConfig.ambientLight}
              position={OFFICE_CAMERA_LIGHT_POSITIONS.ambient}
            />
            <directionalLight
              intensity={officeCamera.viewConfig.directionalLight}
              position={OFFICE_CAMERA_LIGHT_POSITIONS.directional}
            />
            <meshStandardMaterial color="red" />
          </mesh>

          <PerspectiveCamera
            fov={officeCamera.cameraFov}
            makeDefault
            near={officeCamera.cameraNear}
            position={officeCamera.initialCameraPosition}
            ref={officeCamera.cameraRef}
          />
          <MapControls makeDefault ref={officeCamera.orbitControlsRef} />
          <OfficeCameraControlsGuard
            cameraRef={officeCamera.cameraRef}
            effectiveTargetBounds={officeCamera.effectiveTargetBounds}
            orbitControlsRef={officeCamera.orbitControlsRef}
          />
          <CameraDebugger
            orbitControlsRef={officeCamera.orbitControlsRef}
            show={debugMode.includes("camera")}
          />

          <Suspense fallback={<OfficeLoader />}>
            <Bounds maxDuration={0}>
              <OfficeSceneBoundsReader onReady={officeCamera.setOfficeBounds} />
              <OfficeScene />
              <InactivityDetector />
            </Bounds>
          </Suspense>
        </ContextBridge>
      </Canvas>

      {/* <TableCommunication /> */}
      <BottomMenu />
      <RightSideMenu />
      <ChatWindow />
      <ModalRenderer />
      <ContextMenuRenderer />
      <RosterWindow />
      <AvatarMenuWindow />
      <div className="absolute bottom-4 left-4 z-10 flex flex-col items-start gap-[10px]">
        <SidebarAppWarning />
      </div>

      <FeedbackButton />
      <OfficeName />
      <NewVersionAvailableNotification />
      <FluidConnectionStatusIndicator />
    </div>
  );
};
